// ── Landscape Objects Catalog ─────────────────────────────────────────
// ── Module Re-exports (issue #139) ──────────────────────────────────
// Backward-compatible: consumers keep importing from logic.ts.
export {
  pointInPolygon,
  describeArc,
  calculatePolygonArea,
} from './geometry-utils.ts';

export {
  PIPE_MATERIALS,
  STANDARD_PIPE_DIAMETERS,
  calculateFrictionLoss,
  calculatePipeVelocity,
  recommendPipeDiameter,
} from './hydraulics.ts';

export {
  calculatePipeLength,
  getRequiredFittings,
  generateAutoComponents,
} from './bom.ts';

// Internal imports for functions that remain in this file
import { pointInPolygon } from './geometry-utils.ts';
import type { Point } from './geometry-utils.ts';
import {
  calculateFrictionLoss as _calcFrictionLoss,
  METRES_OF_HEAD_PER_BAR,
  calculatePipeVelocity as _calcPipeVelocity,
  recommendPipeDiameter as _recommendPipeDiameter,
} from './hydraulics.ts';
import { calculatePipeLength as _calcPipeLength } from './bom.ts';
import type { Pipe, Zone, DripLine } from './bom.ts';

import type {
  LandscapeObjectDef,
  LandscapeObstacle,
  RotatedRect,
  SprinklerPlacement,
  NozzleArcData,
  NozzlePreset,
  SeriesInfo,
  ZoneStatus,
  SprinklerWithZone,
  ZoneWithPoints,
  PressureCheckResult,
  PipeWarning,
  ZoneHydraulicReport,
  HydraulicReport,
  HeatmapLegendEntry,
  HeatmapResult,
  DripLengthCheck,
  DripTypeInfo,
  WaterSourceProperties,
  WaterSourceCapacityResult,
} from './types.ts';

// ── Landscape Objects Catalog ─────────────────────────────────────────
export const LANDSCAPE_OBJECTS: Record<string, LandscapeObjectDef> = {
  // Circular objects (radius)
  tree:      { shape: 'circle', label: 'tree', color: '#4caf50', defaultRadius: 2.5, icon: '🌳' },
  thuja:     { shape: 'circle', label: 'thuja', color: '#2e7d32', defaultRadius: 1.0, icon: '🌲' },
  bush:      { shape: 'circle', label: 'bush', color: '#66bb6a', defaultRadius: 0.8, icon: '🌿' },
  flower:    { shape: 'circle', label: 'flower', color: '#e91e63', defaultRadius: 0.4, icon: '🌸' },
  pond:      { shape: 'circle', label: 'pond', color: '#2196f3', defaultRadius: 3.0, icon: '💧' },
  gazebo:    { shape: 'circle', label: 'gazebo', color: '#795548', defaultRadius: 2.0, icon: '⛺' },

  // Rectangular objects (width x length)
  pool:      { shape: 'rect', label: 'pool', color: '#1565c0', defaultWidth: 4, defaultLength: 8, icon: '🏊' },
  terrace:   { shape: 'rect', label: 'terrace', color: '#8d6e63', defaultWidth: 4, defaultLength: 6, icon: '🏠' },
  building:  { shape: 'rect', label: 'building', color: '#607d8b', defaultWidth: 10, defaultLength: 12, icon: '🏗️' },
  furniture: { shape: 'rect', label: 'furniture', color: '#9e9e9e', defaultWidth: 2, defaultLength: 3, icon: '🪑' },
  sandbox:   { shape: 'rect', label: 'sandbox', color: '#ffb74d', defaultWidth: 2, defaultLength: 2, icon: '⬜' },

  // Infrastructure objects
  waterSource: { shape: 'circle', label: 'waterSource', color: '#0288d1', defaultRadius: 0.5, icon: '🚰' },
  controller:  { shape: 'rect', label: 'controller', color: '#37474f', defaultWidth: 0.3, defaultLength: 0.4, icon: '🎛️' },
};

// Check if a point is inside a rotated rectangle
export function isPointInRotatedRect({ px, py, obj }: { px: number; py: number; obj: RotatedRect }): boolean {
  const cx = obj.x, cy = obj.y;
  const w = obj.width || 1, h = obj.length || 1;
  const rot = -(obj.rotation || 0) * Math.PI / 180;
  // Translate point relative to rect center, then un-rotate
  const dx = px - cx;
  const dy = py - cy;
  const rx = dx * Math.cos(rot) - dy * Math.sin(rot);
  const ry = dx * Math.sin(rot) + dy * Math.cos(rot);
  return Math.abs(rx) <= w / 2 && Math.abs(ry) <= h / 2;
}

// Check if a point is inside any landscape obstacle (circle or rect)
export function isPointInObstacle({ px, py, obstacles }: { px: number; py: number; obstacles: LandscapeObstacle[] }): boolean {
  for (const obs of obstacles) {
    const def = LANDSCAPE_OBJECTS[obs.type];
    const shape = (def && def.shape) || obs.shape || 'circle';
    if (shape === 'rect') {
      if (isPointInRotatedRect({ px, py, obj: obs })) return true;
    } else {
      const r = obs.crownRadius || obs.radius || 1;
      const d = Math.sqrt((px - obs.x) ** 2 + (py - obs.y) ** 2);
      if (d < r) return true;
    }
  }
  return false;
}

import {
  getSeries as _getSeries,
  getPresets as _getPresets,
  getValves as _getValves,
  getDripTypes as _getDripTypes,
  FALLBACK_PRESET,
} from '../catalog/catalog-loader.ts';

export { getDripTypes } from '../catalog/catalog-loader.ts';

// ── Drip Line Calculations ──────────────────────────────────────────

export function calculateDripEmitters({ dripLine }: { dripLine: DripLine }): number {
  const type = ((_getDripTypes() || {}) as Record<string, DripTypeInfo>)[dripLine.model || ''];
  if (!type || type.emitterSpacing <= 0) return 0;
  const length = _calcPipeLength(dripLine);
  return Math.floor((length * 100) / type.emitterSpacing); // length in m, spacing in cm
}

export function calculateDripFlow({ dripLine }: { dripLine: DripLine }): number {
  const type = ((_getDripTypes() || {}) as Record<string, DripTypeInfo>)[dripLine.model || ''];
  if (!type) return 0;
  if (type.type === "online") return type.flowPerEmitter;
  const emitters = calculateDripEmitters({ dripLine });
  return emitters * type.flowPerEmitter;
}

export function checkDripLineLength({ dripLine }: { dripLine: DripLine }): DripLengthCheck {
  const type = ((_getDripTypes() || {}) as Record<string, DripTypeInfo>)[dripLine.model || ''];
  if (!type || type.maxLength <= 0) return { ok: true };
  const length = _calcPipeLength(dripLine);
  return {
    ok: length <= type.maxLength,
    length: Math.round(length * 10) / 10,
    maxLength: type.maxLength,
  };
}

// ── Preset Helpers ───────────────────────────────────────────────────

export function getPreset(model: string): NozzlePreset {
  const presets = (_getPresets() || {}) as Record<string, NozzlePreset>;
  // Fall through MP1000 → FALLBACK_PRESET so callers never see undefined,
  // even when the catalog API failed to load. Prevents TypeErrors in
  // getArcData / coverage rendering when `preset.arcs[arc]` is read.
  return presets[model] || presets["MP1000"] || (FALLBACK_PRESET as unknown as NozzlePreset);
}

export function getArcData(model: string, arc: number): NozzleArcData {
  const preset = getPreset(model);
  return preset.arcs[arc] || Object.values(preset.arcs)[0];
}

export function getAvailableArcs(model: string): number[] {
  const preset = getPreset(model);
  return Object.keys(preset.arcs).map(Number).sort((a, b) => a - b);
}

export function findClosestArc(model: string, desiredArc: number): number {
  const arcs = getAvailableArcs(model);
  let closest = arcs[0];
  let minDiff = Math.abs(arcs[0] - desiredArc);
  for (const arc of arcs) {
    const diff = Math.abs(arc - desiredArc);
    if (diff < minDiff) {
      minDiff = diff;
      closest = arc;
    }
  }
  return closest;
}

export function getSeriesForModel(model: string): SeriesInfo {
  const preset = getPreset(model);
  const series = (_getSeries() || []) as SeriesInfo[];
  // With the catalog API unreachable there is no list at all and `series[0]`
  // is undefined — every caller then threw reading `.pressure` (the hydraulics
  // panel re-ran it every 600 ms; selecting a head aborted renderAll). No list
  // means no known nominal pressure, which callers already show as a dash and
  // never count as a requirement.
  return series.find((s) => s.key === preset.series) || series[0] || { key: preset.series, pressure: NaN };
}

// ── Zone Logic ───────────────────────────────────────────────────────

export const VALVE_WARNING_THRESHOLD = 0.85;

export function calculateZoneFlow(zone: ZoneWithPoints | Zone, sprinklers: SprinklerWithZone[]): number {
  let total = 0;
  const zoneSprinklers = sprinklers.filter((sp) => sp.zoneId === zone.id);
  zoneSprinklers.forEach((sp) => {
    total += getArcData(sp.model, sp.arc).flow;
  });
  return total;
}

export function getZoneStatus(zone: ZoneWithPoints, sprinklers: SprinklerWithZone[]): ZoneStatus {
  const flow = calculateZoneFlow(zone, sprinklers);
  const count = sprinklers.filter((sp) => sp.zoneId === zone.id).length;
  if (!zone.valveModel) return { status: "no-valve", flow, maxFlow: null, count };
  const valve = ((_getValves() || {}) as Record<string, { maxFlow: number }>)[zone.valveModel];
  if (!valve) return { status: "no-valve", flow, maxFlow: null, count };
  if (flow > valve.maxFlow) return { status: "over-capacity", flow, maxFlow: valve.maxFlow, count };
  if (flow > valve.maxFlow * VALVE_WARNING_THRESHOLD) return { status: "warning", flow, maxFlow: valve.maxFlow, count };
  return { status: "ok", flow, maxFlow: valve.maxFlow, count };
}

export function autoAssignSprinklersToZones(sprinklers: SprinklerWithZone[], zones: ZoneWithPoints[]): SprinklerWithZone[] {
  sprinklers.forEach((sp) => {
    // If manually assigned and zone still exists, keep it
    if (sp._manualZone) {
      if (zones.find((z) => z.id === sp.zoneId)) return;
      // Zone was deleted — clear manual flag and fall through to auto-assign
      sp._manualZone = false;
    }
    // Auto-assign based on position
    sp.zoneId = null;
    for (const zone of zones) {
      if (zone.points.length >= 3 && pointInPolygon(sp.x, sp.y, zone.points)) {
        sp.zoneId = zone.id;
        break;
      }
    }
  });
  return sprinklers;
}

// ── Data Migration ───────────────────────────────────────────────────

export function migrateSprinkler(sp: SprinklerWithZone & { angleEnd?: number; angleStart?: number; type?: string }): SprinklerWithZone {
  if (!sp.model) {
    sp.model = "MP1000";
    sp.arc = (sp.angleEnd || 360) - (sp.angleStart || 0);
    sp.rotation = sp.angleStart || 0;
    delete sp.angleStart;
    delete sp.angleEnd;
    delete sp.type;
  }
  if (sp.rotation === undefined) sp.rotation = 0;
  if (sp.zoneId === undefined) sp.zoneId = null;
  if (sp._manualZone === undefined) sp._manualZone = false;
  return sp;
}

export function migrateZone(zone: ZoneWithPoints): ZoneWithPoints {
  if (zone.valveModel === undefined) zone.valveModel = null;
  if (zone.color === undefined) zone.color = "rgba(156,39,176,0.1)";
  return zone;
}

// ── Flow Calculation ─────────────────────────────────────────────────

export function calculateTotalFlow(sprinklers: SprinklerWithZone[]): number {
  let total = 0;
  sprinklers.forEach((sp) => {
    total += getArcData(sp.model, sp.arc).flow;
  });
  return total;
}

// ── Water Source Defaults & Validation ────────────────────────────────

export const WATER_SOURCE_DEFAULTS: WaterSourceProperties = {
  flow_rate: 30,
  pressure: 3.0,
  source_type: 'municipal',
  elevation: 0,
};

export function validateWaterSourceCapacity({ waterSource, sprinklers }: {
  waterSource: WaterSourceProperties | null;
  sprinklers: SprinklerWithZone[];
}): WaterSourceCapacityResult {
  const totalDemand = calculateTotalFlow(sprinklers);
  if (!waterSource) {
    return { totalDemand, sourceCapacity: 0, sufficient: false, utilizationPercent: 0 };
  }
  const sourceCapacity = waterSource.flow_rate;
  const sufficient = sourceCapacity >= totalDemand;
  const utilizationPercent = sourceCapacity > 0
    ? Math.round((totalDemand / sourceCapacity) * 1000) / 10
    : 0;
  return { totalDemand, sourceCapacity, sufficient, utilizationPercent };
}

// ── Zone Pressure & Hydraulic Report ──────────────────────────────────

// Check if zone has sufficient pressure
export function checkZonePressure({ zone, sprinklers, pipes, sourcePressure = 3.0, elevation = 0 }: {
  zone: ZoneWithPoints | Zone;
  sprinklers: SprinklerWithZone[];
  pipes: (Pipe & { zoneId?: number | null })[];
  sourcePressure?: number;
  elevation?: number;
}): PressureCheckResult {
  const flow = calculateZoneFlow(zone, sprinklers);
  if (flow === 0) {
    return { sufficient: true, available: sourcePressure, required: 0, frictionLoss: 0, elevationLoss: 0 };
  }

  // Sum friction loss across all pipes in zone
  let totalFrictionLoss = 0;
  const zonePipes = pipes.filter((p) => p.zoneId === zone.id);
  zonePipes.forEach((p) => {
    const pipeLength = _calcPipeLength(p);
    const d = p.diameter || 25;
    const material = p.material || 'PE';
    // _calcFrictionLoss returns metres of head; every pressure below is bar.
    totalFrictionLoss +=
      _calcFrictionLoss({ flow, diameter: d, length: pipeLength, material }) / METRES_OF_HEAD_PER_BAR;
  });

  // Elevation loss: 1 bar per 10.2 m of elevation
  const elevationLoss = elevation / 10.2;

  // Min operating pressure for the sprinklers in this zone
  const zoneSprinklers = sprinklers.filter((sp) => sp.zoneId === zone.id);
  let minRequired = 0;
  zoneSprinklers.forEach((sp) => {
    const series = getSeriesForModel(sp.model);
    if (series.pressure > minRequired) minRequired = series.pressure;
  });

  const available = sourcePressure - totalFrictionLoss - elevationLoss;
  return {
    sufficient: available >= minRequired,
    available: Math.round(available * 1000) / 1000,
    required: minRequired,
    frictionLoss: Math.round(totalFrictionLoss * 1000) / 1000,
    elevationLoss: Math.round(elevationLoss * 1000) / 1000,
  };
}

// Full hydraulic report for entire project
export function generateHydraulicReport({ sprinklers, zones, pipes, sourcePressure = 3.0 }: {
  sprinklers: SprinklerWithZone[];
  zones: ZoneWithPoints[];
  pipes: (Pipe & { zoneId?: number | null })[];
  sourcePressure?: number;
}): HydraulicReport {
  const zoneReports: ZoneHydraulicReport[] = zones.map((zone) => {
    const flow = calculateZoneFlow(zone, sprinklers);
    const pressureCheck = checkZonePressure({ zone, sprinklers, pipes, sourcePressure });
    const zoneSprinklers = sprinklers.filter((sp) => sp.zoneId === zone.id);
    const recommendedDiameter = _recommendPipeDiameter({ flow });

    // Check pipe velocities
    const zonePipes = pipes.filter((p) => p.zoneId === zone.id);
    const pipeWarnings: PipeWarning[] = [];
    zonePipes.forEach((p) => {
      const d = p.diameter || 25;
      const v = _calcPipeVelocity({ flow, diameter: d });
      if (v > 1.5) {
        pipeWarnings.push({
          pipeId: p.id,
          velocity: Math.round(v * 100) / 100,
          diameter: d,
          recommendedDiameter: _recommendPipeDiameter({ flow }),
        });
      }
    });

    return {
      zoneId: zone.id,
      zoneName: zone.name,
      flow,
      sprinklerCount: zoneSprinklers.length,
      pressure: pressureCheck,
      recommendedDiameter,
      pipeWarnings,
    };
  });

  const totalFlow = calculateTotalFlow(sprinklers);

  return {
    totalFlow,
    sourcePressure,
    zones: zoneReports,
  };
}

// ── AI Auto-Placement of Sprinklers ──────────────────────────────────

// Calculate distance between two points
function distance(a: Point, b: Point): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

// Project point onto line segment, returning closest point on segment
function projectPointOnSegment(p: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

// Calculate distance from point to polygon edge
export function distanceToPolygonEdge({ point, polygon }: { point: Point; polygon: Point[] }): number {
  if (!polygon || polygon.length < 2) return Infinity;
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const proj = projectPointOnSegment(point, a, b);
    const d = distance(point, proj);
    if (d < minDist) minDist = d;
  }
  return minDist;
}

// Calculate angle between two boundary edges meeting at a vertex
function vertexAngle(prev: Point, vertex: Point, next: Point): number {
  const v1x = prev.x - vertex.x;
  const v1y = prev.y - vertex.y;
  const v2x = next.x - vertex.x;
  const v2y = next.y - vertex.y;
  const dot = v1x * v2x + v1y * v2y;
  const cross = v1x * v2y - v1y * v2x;
  const angle = Math.atan2(Math.abs(cross), dot);
  return (angle * 180) / Math.PI;
}

export function autoPlaceSprinklers({ boundary, obstacles = [], model = 'MP3000', preferredArc: _preferredArc = 360, spacing = null, gridType = 'square' }: {
  boundary: Point[];
  obstacles?: LandscapeObstacle[];
  model?: string;
  preferredArc?: number;
  spacing?: number | null;
  gridType?: string;
}): SprinklerPlacement[] {
  if (!boundary || boundary.length < 3) return [];

  // Check degenerate polygon (all points same)
  const allSame = boundary.every(p => p.x === boundary[0].x && p.y === boundary[0].y);
  if (allSame) return [];

  const preset = ((_getPresets() || {}) as Record<string, NozzlePreset>)[model];
  if (!preset) return [];

  // Use highest available arc data for radius
  const arcs = Object.keys(preset.arcs).map(Number).sort((a, b) => a - b);
  const maxArcData = preset.arcs[arcs[arcs.length - 1]];
  const radius = maxArcData.radius;

  // Head-to-head spacing: each sprinkler's throw should reach its neighbor
  const effectiveSpacing = spacing || radius * 1.0;

  // Calculate bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of boundary) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  const clearance = radius * 0.3;
  const sprinklers: SprinklerPlacement[] = [];
  const usedPositions = new Set<string>();

  function posKey(x: number, y: number): string {
    return `${Math.round(x * 100)},${Math.round(y * 100)}`;
  }

  function isInsideAnyObstacle(x: number, y: number): boolean {
    return isPointInObstacle({ px: x, py: y, obstacles });
  }

  // `rotation` in the planner is the compass bearing of the arc's centre —
  // 0° = north, clockwise — which is how renderCoverage, the heatmap and the 3D
  // view all read it. The inward directions below come from atan2 in the
  // y-down plane (0° = east, clockwise), a quarter turn away. Emitting the
  // arc's start from east instead aimed every corner and edge head out of the
  // plot.
  const bearingOf = (eastDeg: number) => eastDeg + 90;

  function addSprinkler(x: number, y: number, arc: number, rotation: number): void {
    const key = posKey(x, y);
    if (usedPositions.has(key)) return;
    usedPositions.add(key);

    const closestArc = findClosestArc(model, arc);
    const arcData = getArcData(model, closestArc);

    sprinklers.push({
      x, y, model,
      arc: closestArc,
      radius: arcData.radius,
      rotation: ((rotation % 360) + 360) % 360,
    });
  }

  // Step 1: Corner sprinklers — at boundary vertices with angles <= 120°
  for (let i = 0; i < boundary.length; i++) {
    const prev = boundary[(i - 1 + boundary.length) % boundary.length];
    const curr = boundary[i];
    const next = boundary[(i + 1) % boundary.length];
    const angle = vertexAngle(prev, curr, next);

    if (angle <= 120) {
      if (isInsideAnyObstacle(curr.x, curr.y)) continue;
      // Rotation: bisector of the two edges, pointing inward
      const v1x = prev.x - curr.x;
      const v1y = prev.y - curr.y;
      const v2x = next.x - curr.x;
      const v2y = next.y - curr.y;
      const len1 = Math.sqrt(v1x * v1x + v1y * v1y) || 1;
      const len2 = Math.sqrt(v2x * v2x + v2y * v2y) || 1;
      const bisX = v1x / len1 + v2x / len2;
      const bisY = v1y / len1 + v2y / len2;
      // Check if bisector points inward — test a point slightly along bisector
      const testX = curr.x + bisX * 0.1;
      const testY = curr.y + bisY * 0.1;
      let rot: number;
      if (pointInPolygon(testX, testY, boundary)) {
        rot = (Math.atan2(bisY, bisX) * 180) / Math.PI;
      } else {
        rot = (Math.atan2(-bisY, -bisX) * 180) / Math.PI;
      }
      addSprinkler(curr.x, curr.y, 90, bearingOf(rot));
    }
  }

  // Step 2: Edge sprinklers — walk along boundary edges placing 180° sprinklers
  for (let i = 0; i < boundary.length; i++) {
    const a = boundary[i];
    const b = boundary[(i + 1) % boundary.length];
    const edgeLen = distance(a, b);
    if (edgeLen < effectiveSpacing * 0.5) continue;

    const dx = (b.x - a.x) / edgeLen;
    const dy = (b.y - a.y) / edgeLen;

    // Normal pointing inward
    const nx = -dy;
    const ny = dx;
    const testX = (a.x + b.x) / 2 + nx * 0.1;
    const testY = (a.y + b.y) / 2 + ny * 0.1;
    let inwardNx = nx, inwardNy = ny;
    if (!pointInPolygon(testX, testY, boundary)) {
      inwardNx = -nx;
      inwardNy = -ny;
    }
    const edgeRot = (Math.atan2(inwardNy, inwardNx) * 180) / Math.PI;

    // Place sprinklers along the edge at spacing intervals, skip corners
    const startOffset = effectiveSpacing * 0.5;
    for (let d = startOffset; d < edgeLen - startOffset * 0.5; d += effectiveSpacing) {
      const px = a.x + dx * d;
      const py = a.y + dy * d;
      if (isInsideAnyObstacle(px, py)) continue;

      // Check not too close to existing corner sprinklers
      const tooCloseToCorner = sprinklers.some(s => {
        const dist = distance({ x: px, y: py }, s);
        return dist < effectiveSpacing * 0.4;
      });
      if (tooCloseToCorner) continue;

      addSprinkler(px, py, 180, bearingOf(edgeRot));
    }
  }

  // Step 3: Interior sprinklers — grid fill with 360° sprinklers
  const rowSpacing = gridType === 'triangular'
    ? effectiveSpacing * Math.sqrt(3) / 2
    : effectiveSpacing;

  let rowIndex = 0;
  for (let y = minY + effectiveSpacing; y < maxY; y += rowSpacing) {
    const xOffset = (gridType === 'triangular' && rowIndex % 2 === 1)
      ? effectiveSpacing / 2
      : 0;

    for (let x = minX + effectiveSpacing + xOffset; x < maxX; x += effectiveSpacing) {
      if (!pointInPolygon(x, y, boundary)) continue;
      if (isInsideAnyObstacle(x, y)) continue;

      const edgeDist = distanceToPolygonEdge({ point: { x, y }, polygon: boundary });
      if (edgeDist < clearance) continue;

      // Check not too close to existing sprinklers
      const tooClose = sprinklers.some(s => {
        const dist = distance({ x, y }, s);
        return dist < effectiveSpacing * 0.6;
      });
      if (tooClose) continue;

      addSprinkler(x, y, 360, 0);
    }
    rowIndex++;
  }

  return sprinklers;
}

// ── Precipitation Heatmap ─────────────────────────────────────────────

/**
 * Check if a point falls within a sprinkler's coverage arc.
 */
export function isPointInSprinklerArc({ px, py, sprinkler }: {
  px: number;
  py: number;
  sprinkler: { x: number; y: number; radius: number; arc: number; rotation?: number };
}): boolean {
  const dx = px - sprinkler.x;
  const dy = py - sprinkler.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist > sprinkler.radius) return false;
  if (sprinkler.arc >= 360) return true;

  // Calculate angle from sprinkler to point (0° = North, clockwise)
  const angle = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;

  // Check if angle is within rotation ± arc/2
  const halfArc = sprinkler.arc / 2;
  const rotation = sprinkler.rotation || 0;
  const startAngle = (rotation - halfArc + 360) % 360;
  const endAngle = (rotation + halfArc) % 360;

  if (startAngle <= endAngle) {
    return angle >= startAngle && angle <= endAngle;
  } else {
    return angle >= startAngle || angle <= endAngle;
  }
}

/** Upper edge, in mm/h, of heatmap levels 1–4; level 5 is anything above. */
const HEATMAP_BAND_EDGES_MMH = [10, 20, 30, 40];

/** The heatmap level (0–5) a precipitation rate in mm/h is painted with. */
export function heatmapLevel(precipMmh: number): number {
  if (precipMmh <= 0) return 0;
  const band = HEATMAP_BAND_EDGES_MMH.findIndex((edge) => precipMmh <= edge);
  return band === -1 ? HEATMAP_BAND_EDGES_MMH.length + 1 : band + 1;
}

/**
 * Build the heatmap legend entries array. The band edges travel as numbers
 * rather than inside the translations, so the label can state them in the
 * user's units — written into the strings they read "≤10 in/h" for a band
 * that ends at 10 mm/h.
 */
export function getHeatmapLegend(): HeatmapLegendEntry[] {
  const [e1, e2, e3, e4] = HEATMAP_BAND_EDGES_MMH;
  return [
    { level: 0, color: "transparent",            labelKey: "sidebar.heatmapNone" },
    { level: 1, color: "rgba(173,216,230,0.4)",  labelKey: "sidebar.heatmapLow",      max: e1 },
    { level: 2, color: "rgba(0,150,255,0.5)",    labelKey: "sidebar.heatmapMedium",   min: e1, max: e2 },
    { level: 3, color: "rgba(0,100,200,0.6)",    labelKey: "sidebar.heatmapHigh",     min: e2, max: e3 },
    { level: 4, color: "rgba(0,50,180,0.7)",     labelKey: "sidebar.heatmapVeryHigh", min: e3, max: e4 },
    { level: 5, color: "rgba(180,0,0,0.7)",      labelKey: "sidebar.heatmapExcess",   min: e4 },
  ];
}

/**
 * Calculate precipitation heatmap for the project.
 * Divides the plot into a grid and calculates total precipitation at each point
 * based on which sprinkler arcs cover it.
 */
export function calculatePrecipitationHeatmap({ sprinklers, boundary, plotWidth, plotHeight, resolution = 0.5 }: {
  sprinklers: SprinklerWithZone[];
  boundary: Point[];
  plotWidth: number;
  plotHeight: number;
  resolution?: number;
}): HeatmapResult {
  // Expand grid to cover the full boundary and all sprinkler coverage areas
  let effectiveWidth = plotWidth;
  let effectiveHeight = plotHeight;
  if (boundary && boundary.length >= 3) {
    for (const p of boundary) {
      if (p.x > effectiveWidth) effectiveWidth = p.x;
      if (p.y > effectiveHeight) effectiveHeight = p.y;
    }
  }
  for (const sp of (sprinklers || [])) {
    const reachX = sp.x + (sp.radius || 0);
    const reachY = sp.y + (sp.radius || 0);
    if (reachX > effectiveWidth) effectiveWidth = reachX;
    if (reachY > effectiveHeight) effectiveHeight = reachY;
  }

  const cols = Math.ceil(effectiveWidth / resolution);
  const rows = Math.ceil(effectiveHeight / resolution);
  const grid: number[][] = [];
  let minPrecip = Infinity;
  let maxPrecip = 0;

  // Pre-compute precipitation rate for each sprinkler.
  // Default radius to 0 — isPointInSprinklerArc treats 0 as "no coverage".
  const spData = (sprinklers || []).map((sp) => {
    const arcData = getArcData(sp.model, sp.arc);
    return { ...sp, radius: sp.radius ?? 0, precip: arcData.precip || 0 };
  });

  for (let r = 0; r < rows; r++) {
    const row: number[] = [];
    for (let c = 0; c < cols; c++) {
      const px = (c + 0.5) * resolution;
      const py = (r + 0.5) * resolution;

      // Skip points outside boundary if boundary is defined
      if (boundary && boundary.length >= 3 && !pointInPolygon(px, py, boundary)) {
        row.push(0);
        continue;
      }

      let totalPrecip = 0;
      for (const sp of spData) {
        if (isPointInSprinklerArc({ px, py, sprinkler: sp })) {
          totalPrecip += sp.precip;
        }
      }

      row.push(totalPrecip);
      if (totalPrecip > 0 && totalPrecip < minPrecip) minPrecip = totalPrecip;
      if (totalPrecip > maxPrecip) maxPrecip = totalPrecip;
    }
    grid.push(row);
  }

  if (minPrecip === Infinity) minPrecip = 0;

  return {
    grid,
    minPrecip,
    maxPrecip,
    cellSize: resolution,
    legend: getHeatmapLegend(),
  };
}
